import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { SYSTEM_FLOW_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * system_flow — reusable cause→effect pipeline (content is JSON-only).
 *
 * Reuse across worlds: robotics sensor→controller→motor; space camera→computer→radio→antenna;
 * animals food→energy→movement; electricity battery→switch→bulb.
 *
 * activityData.content schema:
 * {
 *   instructions?: string,
 *   runCaption?: string,          // shown during / after run animation
 *   nodes: [
 *     { id: string, label: string, emoji?: string }
 *   ],
 *   correctOrder: string[],       // node ids left-to-right
 *   initialOrder?: string[],      // optional start permutation (default: reverse of correctOrder)
 *   victory?: { message?: string }
 * }
 *
 * Learner reorders with ↑↓, taps Run when order matches correctOrder; a pulse animates along the chain.
 */

function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function pickSystemFlowTitle(titleProp, guidedContext) {
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const activityTitle = typeof titleProp === 'string' ? titleProp.trim() : '';
  if (guidedContext?.singleSurface === true && stepTitle.trim()) {
    if (!activityTitle) return null;
    if (shouldSuppressActivityTitle({ stepTitle, activityTitle: titleProp })) return null;
    return activityTitle;
  }
  if (activityTitle) return activityTitle;
  const fallback = typeof titleProp === 'string' && titleProp.trim() ? titleProp.trim() : 'System flow';
  return fallback;
}

function pickInstructionLine(guidedContext, rawInstructions, compactLesson) {
  const readable =
    typeof guidedContext?.readableInstruction === 'string' ? guidedContext.readableInstruction.trim() : '';
  const lead = typeof guidedContext?.stepInstructionLead === 'string' ? guidedContext.stepInstructionLead.trim() : '';
  const shellOneShot = compactLesson && guidedContext?.singleSurface === true;

  if (shellOneShot) {
    if (readable) return readable;
    if (lead) return lead;
    if (rawInstructions) return rawInstructions;
    return '';
  }
  if (lead) return lead;
  if (rawInstructions) return rawInstructions;
  return '';
}

export default function SystemFlowEngine({
  title = 'System flow',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_system_flow',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const nodes = Array.isArray(content.nodes) ? content.nodes : [];
  const correctOrder = Array.isArray(content.correctOrder) ? content.correctOrder : [];
  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);

  const defaultInitial = useMemo(() => {
    if (Array.isArray(content.initialOrder) && content.initialOrder.length) return [...content.initialOrder];
    return [...correctOrder].reverse();
  }, [content.initialOrder, correctOrder]);

  const [order, setOrder] = useState(() => [...defaultInitial]);
  const [completed, setCompleted] = useState(false);
  const [running, setRunning] = useState(false);
  const [pulseIdx, setPulseIdx] = useState(-1);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');

  const singleSurface = guidedContext?.singleSurface === true;
  const tapCompact = singleSurface && isTapFirstLessonUi(lessonUiMode);
  const shellDeferRun = typeof onRegisterLessonFooterActions === 'function' && singleSurface;

  const compactLesson = isTapFirstLessonUi(lessonUiMode);
  const rawInstructions = typeof content.instructions === 'string' ? content.instructions.trim() : '';

  const headline = useMemo(() => pickSystemFlowTitle(title, guidedContext), [title, guidedContext]);
  const instructionBody = useMemo(
    () => pickInstructionLine(guidedContext, rawInstructions, compactLesson),
    [guidedContext, rawInstructions, compactLesson],
  );

  const isCorrect = nodes.length > 0 && correctOrder.length === nodes.length && arraysEqual(order, correctOrder);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: SYSTEM_FLOW_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteCorrectAnswer, resetSession } = guided;

  const move = (idx, dir) => {
    if (completed || running) return;
    noteUserAction();
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    setOrder((prev) => {
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    onProgress?.({ activityId, engine: 'system_flow', phase: 'reorder' });
  };

  const runFlow = useCallback(() => {
    if (completed || running || !isCorrect) return;
    noteUserAction();
    setRunning(true);
    setPulseIdx(0);
    setA11y('Running the system.');
    let i = 0;
    const tick = () => {
      i += 1;
      if (i >= order.length) {
        setPulseIdx(-1);
        setRunning(false);
        setCompleted(true);
        noteCorrectAnswer();
        celebrate();
        setA11y('Signal reached the end.');
        trackMiniAppEvent.completed(activityId);
        onComplete?.({ activityId, engine: 'system_flow', perfect: true });
        return;
      }
      setPulseIdx(i);
      window.setTimeout(tick, 420);
    };
    window.setTimeout(tick, 280);
  }, [
    activityId,
    celebrate,
    completed,
    isCorrect,
    noteCorrectAnswer,
    noteUserAction,
    onComplete,
    order.length,
    running,
  ]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    setOrder([...correctOrder]);
    setPulseIdx(-1);
    setRunning(false);
  }, [completed, correctOrder, noteUserAction]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions || !shellDeferRun) return undefined;
    if (completed) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    const showAnswer = Array.isArray(hints) && hints.length > 0 ? revealAnswer : undefined;
    onRegisterLessonFooterActions({
      checkAnswer: runFlow,
      checkAnswerDisabled: running || !isCorrect,
      checkAnswerLabel: running ? 'Running…' : 'Run system',
      ...(showAnswer ? { showAnswer } : {}),
    });
    return () => onRegisterLessonFooterActions(null);
  }, [
    completed,
    hints.length,
    isCorrect,
    onRegisterLessonFooterActions,
    revealAnswer,
    runFlow,
    running,
    shellDeferRun,
  ]);

  const reset = () => {
    if (completed) return;
    resetSession();
    setOrder([...defaultInitial]);
    setPulseIdx(-1);
    setRunning(false);
  };

  const titleStyleUse =
    tapCompact && headline
      ? { ...titleStyle, margin: '0 0 6px', fontSize: 'clamp(1rem, 3vw, 1.18rem)' }
      : titleStyle;
  const instructStyleUse = tapCompact
    ? { ...instructionsStyle, fontSize: 14, lineHeight: 1.35, marginBottom: 8 }
    : instructionsStyle;

  const gridStyle = tapCompact
    ? {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 8,
        marginBottom: 10,
      }
    : {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'stretch',
        justifyContent: 'center',
        marginBottom: 14,
      };

  const tileEmojiSize = tapCompact ? 22 : 28;
  const tileLabelSize = tapCompact ? 12 : 14;
  const tilePad = tapCompact ? '6px 4px' : '10px 8px';
  const tileFlex = tapCompact ? undefined : '1 1 120px';
  const tileMaxW = tapCompact ? 'none' : 160;
  const btnW = tapCompact ? 38 : 44;
  const btnH = tapCompact ? 34 : 40;

  const rootStyle = {
    ...getEngineRootStyle({ singleSurface }),
    position: 'relative',
    ...(guided.contentReserveStyle || {}),
  };

  return (
    <div style={rootStyle}>
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'Nice flow!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {headline ? <h2 style={titleStyleUse}>{headline}</h2> : null}
      {instructionBody ? <p style={instructStyleUse}>{instructionBody}</p> : null}

      <div style={gridStyle}>
        {order.map((id, idx) => {
          const n = nodeById[id];
          if (!n) return null;
          const hot = pulseIdx === idx;
          return (
            <div
              key={`${id}-${idx}`}
              style={{
                ...(tileFlex ? { flex: tileFlex, maxWidth: tileMaxW } : {}),
                borderRadius: tapCompact ? 12 : 14,
                border: `2px solid ${hot ? 'var(--color-primary)' : 'var(--color-border)'}`,
                background: hot ? 'rgba(99,102,241,0.18)' : 'var(--color-bg)',
                padding: tilePad,
                textAlign: 'center',
                boxShadow: hot ? '0 0 0 2px rgba(99,102,241,0.35)' : 'none',
                transition: 'background 0.2s, border-color 0.2s',
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: tileEmojiSize, lineHeight: 1.2, marginBottom: tapCompact ? 2 : 4 }} aria-hidden>
                {n.emoji || '▫️'}
              </div>
              <div style={{ fontWeight: 800, fontSize: tileLabelSize, color: 'var(--color-text)', lineHeight: 1.2 }}>
                {n.label}
              </div>
              {!completed && (
                <div style={{ display: 'flex', gap: tapCompact ? 4 : 6, marginTop: tapCompact ? 6 : 8, justifyContent: 'center' }}>
                  <button
                    type="button"
                    aria-label="Move earlier"
                    disabled={running || idx === 0}
                    onClick={() => move(idx, -1)}
                    style={{
                      ...secondaryBtn,
                      width: btnW,
                      minHeight: btnH,
                      padding: tapCompact ? 4 : 8,
                      flex: '0 0 auto',
                    }}
                  >
                    <ChevronUp size={tapCompact ? 16 : 18} aria-hidden style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                  <button
                    type="button"
                    aria-label="Move later"
                    disabled={running || idx === order.length - 1}
                    onClick={() => move(idx, 1)}
                    style={{
                      ...secondaryBtn,
                      width: btnW,
                      minHeight: btnH,
                      padding: tapCompact ? 4 : 8,
                      flex: '0 0 auto',
                    }}
                  >
                    <ChevronDown size={tapCompact ? 16 : 18} aria-hidden style={{ transform: 'rotate(-90deg)' }} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isCorrect && !completed && !shellDeferRun && (
        <p style={{ ...feedbackCorrect, marginBottom: 10 }}>Order looks right — run the system!</p>
      )}
      {content.runCaption && (running || completed) && (
        <p style={{ ...instructStyleUse, fontWeight: 700, marginTop: 0 }}>{content.runCaption}</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: tapCompact ? 8 : 10 }}>
        {!shellDeferRun && (
          <button type="button" style={primaryBtn} disabled={completed || running || !isCorrect} onClick={runFlow}>
            {running ? 'Running…' : 'Run system'}
          </button>
        )}
        {!completed && (
          <button type="button" style={secondaryBtn} onClick={reset} disabled={running}>
            Reset order
          </button>
        )}
      </div>
    </div>
  );
}
